/**
 * @typedef {import('../PropertyProvider/State').Property} Property
 * @typedef {import('./propertyCategories').PropertyCategory} PropertyCategory
 * @typedef {Partial<import('../../../Auth/AuthService/AuthService').default>} AuthService
 * @typedef {import('../../shared/PropertyService/EditablePropertyDetails').default} EditablePropertyDetails
 * @typedef {NonNullable<EditablePropertyDetails['details']>} EditablePropertyFields
 * @typedef {import('../../shared/PropertyService/PropertyService').default} PropertyService
 * @typedef {Readonly<{authService: AuthService, property: Readonly<import('../PropertyProvider/PropertyContext').default>, propertyService: PropertyService, history: {push: (path: string) => void}}>} Props
 * @typedef {Readonly<{propertyImages: Record<string, string>, watchListDetails: Record<string, WatchListDetails>, categoryErrorByPropertyId: Record<string, string | null>, isUpdatingCategoryByPropertyId: Record<string, boolean>} >} State
 * @typedef {Readonly<{baths?: string | number | null, beds?: string | number | null, price?: number | null, sqft?: string | number | null}>} WatchListDetails
 * @typedef {Readonly<{valueMean?: number | null}>} Valuation
 * @typedef {Readonly<Map<string, Valuation[]> | {values: () => Iterable<Valuation[]>}>} ValuationCollection
 * @typedef {Readonly<{
 * 	bathroom_count?: string | number | null;
 * 	bathroomCount?: string | number | null;
 * 	bathrooms?: string | number | null;
 * 	bedroom_count?: string | number | null;
 * 	bedroomCount?: string | number | null;
 * 	bedrooms?: string | number | null;
 * 	details?: WatchListRawDetails | null;
 * 	lot_size_square_feet?: string | number | null;
 * 	lotsizeSquareFeet?: string | number | null;
 * 	number_of_baths?: string | number | null;
 * 	number_of_bedrooms?: string | number | null;
 * 	numberOfBaths?: string | number | null;
 * 	numberOfBedrooms?: string | number | null;
 * 	propertyDetails?: WatchListRawPropertyDetails | null;
 * 	sqft?: string | number | null;
 * 	square_footage?: string | number | null;
 * 	squareFootage?: string | number | null;
 * }>} WatchListRawProperty
 * @typedef {Readonly<{number_of_baths?: string | number | null, numberOfBaths?: string | number | null, number_of_bedrooms?: string | number | null, numberOfBedrooms?: string | number | null, lot_size_square_feet?: string | number | null, lotSizeSquareFootage?: string | number | null, rooms?: WatchListRooms | null, size?: WatchListSize | null}>} WatchListRawDetails
 * @typedef {Readonly<{bathroomCount?: string | number | null, bedroomCount?: string | number | null, lotsizeSquareFeet?: string | number | null, numberOfBaths?: string | number | null, numberOfBedrooms?: string | number | null, squareFootage?: string | number | null}>} WatchListRawPropertyDetails
 * @typedef {Readonly<{bathrooms?: string | number | null, bedrooms?: string | number | null}>} WatchListRooms
 * @typedef {Readonly<{land?: {squareFootage?: string | number | null} | null}>} WatchListSize
 */
import {
	PROPERTY_CATEGORIES,
	UNCATEGORIZED_CATEGORY_KEY,
	getPropertyCategory,
} from './propertyCategories';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../../shared/AuthProvider';
import { withProperty } from '../PropertyProvider';
import { withPropertyService } from '../../../service-container';
import BaseForm from '../../../shared/Forms/BaseForm';
import FieldSelect from '../../../shared/Forms/Fields/FieldSelect';
import formatCentsForDisplay from '../../../utils/formatCentsForDisplay';
import formatAddressToString from '../../../utils/formatAddressToString';
import images from '../../../config/local/images';
import LinkButton from '../../../shared/LinkButton';
import Loading from '../../../shared/Loading';
import paths from '../../../config/local/paths';
import PropertyMap from './PropertyMap';
import React from 'react';
import SelectOptionList from '../../../shared/Forms/Inputs/SelectOptionList';
import { toAbsoluteUrl } from '../../../utils/toAbsoluteUrl';
import { translate } from '../../Internationalization';
import * as homeownerPropertyService from './property-service';

/** @typedef {Readonly<{ key: PropertyCategory | 'uncategorized', title: string }>} CategoryConfig */

/** @type {Readonly<CategoryConfig[]>} */
const CATEGORY_CONFIG = [
	{
		key: PROPERTY_CATEGORIES.PRIMARY_HOME,
		title: translate(
			'homeowner.pages.properties.scoped.category.primary_home'
		),
	},
	{
		key: PROPERTY_CATEGORIES.SECOND_HOME,
		title: translate(
			'homeowner.pages.properties.scoped.category.second_home'
		),
	},
	{
		key: PROPERTY_CATEGORIES.FORMER_HOME,
		title: translate(
			'homeowner.pages.properties.scoped.category.former_home'
		),
	},
	{
		key: PROPERTY_CATEGORIES.FRIENDS_OR_FAMILY_HOME,
		title: translate(
			'homeowner.pages.properties.scoped.category.friends_or_family'
		),
	},
	{
		key: UNCATEGORIZED_CATEGORY_KEY,
		title: translate(
			'homeowner.pages.properties.scoped.category.uncategorized'
		),
	},
	{
		key: PROPERTY_CATEGORIES.WATCH_LIST,
		title: translate(
			'homeowner.pages.properties.scoped.category.watch_list'
		),
	},
];

/**
 * @param {string | number | null | undefined | {total?: string | number | null, full?: string | number | null}} value
 * @returns {string | number | null | undefined}
 */
function normalizeWatchListValue(value) {
	if (typeof value === 'object' && value !== null) {
		return value.total ?? value.full;
	}

	return value;
}

/**
 * @param {string | number | null | undefined | {total?: string | number | null, full?: string | number | null}} value
 * @returns {string | number}
 */
function formatWatchListValue(value) {
	let normalizedValue = normalizeWatchListValue(value);

	if (typeof normalizedValue === 'number') {
		normalizedValue = normalizedValue.toLocaleString('en-US');
	}

	if (
		normalizedValue === null ||
		normalizedValue === undefined ||
		normalizedValue === ''
	) {
		return '--';
	}

	return normalizedValue;
}

/**
 * @param {ValuationCollection | null | undefined} valuations
 * @returns {number | null}
 */
function getLatestValuationAmount(valuations) {
	if (!valuations) {
		return null;
	}

	const latest = [...valuations.values()]
		.flat()
		.find((valuation) => valuation?.valueMean);

	return latest?.valueMean ?? null;
}

/**
 * @param {PropertyService} propertyService
 * @param {string} propertyId
 * @returns {Promise<{
 * 	readonly details: EditablePropertyDetails | null;
 * 	readonly rawProperty: WatchListRawProperty | null;
 * 	readonly valuations: ValuationCollection | null;
 * }>}
 */
async function getWatchListDetails(propertyService, propertyId) {
	const [details, rawProperty, valuations] = await Promise.all([
		propertyService.getEditablePropertyById(propertyId).catch(() => null),
		propertyService.getPropertyById(propertyId).catch(() => null),
		propertyService.getValuations(propertyId).catch(() => null),
	]);

	return { details, rawProperty, valuations };
}

/**
 * @param {...(string | number | null | undefined)} values
 * @returns {string | number | null | undefined}
 */
function pickFirstValue(...values) {
	return values.find(
		(value) => value !== null && value !== undefined && value !== ''
	);
}

/**
 * @param {string | number | null | undefined} value
 * @returns {number | null}
 */
function toNumberOrNull(value) {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	const parsedValue = Number(value);
	return Number.isFinite(parsedValue) ? parsedValue : null;
}

/**
 * @param {string | number | null | undefined} value
 * @returns {string | null}
 */
function toNullableString(value) {
	if (value === '' || value === undefined || value === null) {
		return null;
	}

	return String(value);
}

/**
 * @param {EditablePropertyFields} values
 * @returns {EditablePropertyFields}
 */
function buildPropertyDetails(values) {
	return {
		acreage: toNumberOrNull(values.acreage),
		amountToLoan: toNumberOrNull(values.amountToLoan),
		basement: toNullableString(values.basement),
		bedrooms: toNumberOrNull(values.bedrooms),
		buildYear: toNumberOrNull(values.buildYear),
		constructionType: toNullableString(values.constructionType),
		dueDate: toNullableString(values.dueDate),
		fireplace: toNullableString(values.fireplace),
		floorCover: toNullableString(values.floorCover),
		foundationType: toNullableString(values.foundationType),
		garageCars: toNumberOrNull(values.garageCars),
		garageType: toNullableString(values.garageType),
		heating: toNullableString(values.heating),
		heatingFuelType: toNullableString(values.heatingFuelType),
		interiorWalls: toNullableString(values.interiorWalls),
		lenderName: toNullableString(values.lenderName),
		loanType: toNullableString(values.loanType),
		lotSizeSquareFootage: toNumberOrNull(values.lotSizeSquareFootage),
		maximumInterestRate: toNumberOrNull(values.maximumInterestRate),
		numberOfBaths: toNumberOrNull(values.numberOfBaths),
		numberOfPartialBaths: toNumberOrNull(values.numberOfPartialBaths),
		originalDateOfContract: toNullableString(values.originalDateOfContract),
		otherRooms: toNumberOrNull(values.otherRooms),
		plumbingFixtures: toNullableString(values.plumbingFixtures),
		pool: toNullableString(values.pool),
		rate: toNumberOrNull(values.rate),
		roofCover: toNullableString(values.roofCover),
		roofType: toNullableString(values.roofType),
		sewer: toNullableString(values.sewer),
		stories: toNumberOrNull(values.stories),
		style: toNullableString(values.style),
		totalNumberOfRooms: toNumberOrNull(values.totalNumberOfRooms),
		water: toNullableString(values.water),
	};
}

/**
 * @param {EditablePropertyDetails} property
 * @param {PropertyCategory} category
 * @returns {EditablePropertyDetails}
 */
function buildPropertyPayload(property, category) {
	return {
		address: {
			city: toNullableString(property?.address?.city),
			country: toNullableString(property?.address?.country),
			postalCode: toNullableString(property?.address?.postalCode),
			state: toNullableString(property?.address?.state),
			streetAddress1: toNullableString(property?.address?.streetAddress1),
			streetAddress2: toNullableString(property?.address?.streetAddress2),
		},
		category,
		details: buildPropertyDetails(
			/** @type {EditablePropertyFields} */ (property.details ?? {})
		),
		title: toNullableString(property?.title),
	};
}

/**
 * @param {boolean} isUpdating
 * @param {string | null} categoryError
 * @returns {JSX.Element | null}
 */
function renderCategoryStatus(isUpdating, categoryError) {
	if (isUpdating) {
		return (
			<section className="flex items-center justify-center h-8 overflow-hidden">
				<span className="block w-fit -scale-50 [&>div]:p-0">
					<Loading />
				</span>
			</section>
		);
	}

	if (categoryError) {
		return <p className="text-danger text-sm mb-0">{categoryError}</p>;
	}

	return null;
}

/**
 * @param {CategoryConfig} category
 * @returns {PropertyCategory | null}
 */
function getAddCardCategoryState(category) {
	if (category.key === UNCATEGORIZED_CATEGORY_KEY) {
		return null;
	}

	return category.key;
}

/**
 * @param {CategoryConfig} category
 * @returns {JSX.Element | null}
 */
function renderCategoryAddButton(category) {
	const stateCategory = getAddCardCategoryState(category);

	if (stateCategory === null) {
		return null;
	}

	return (
		<LinkButton
			className="button inline-block !mb-0 !px-5 !py-2"
			outline={false}
			to={{
				pathname: paths.app.homeowner.properties.add,
				state: { category: stateCategory },
			}}
		>
			{translate('homeowner.pages.properties.scoped.labels.add')}
		</LinkButton>
	);
}

/** @extends {React.Component<Props, State>} */
class PropertiesHub extends React.Component {
	static contextType = AuthContext;

	/** @type {import('../../../shared/AuthProvider/State').default | null} */
	context = null;

	/** @type {number} */
	#propertyResourceRequestId = 0;

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			categoryErrorByPropertyId: {},
			isUpdatingCategoryByPropertyId: {},
			propertyImages: {},
			watchListDetails: {},
		};
	}

	/** @returns {Promise<void>} */
	async componentDidMount() {
		await this.#setPropertyImages();
	}

	/** @param {Props} prevProps */
	async componentDidUpdate(prevProps) {
		if (
			prevProps.property.propertyList !== this.props.property.propertyList
		) {
			await this.#setPropertyImages();
		}
	}

	/**
	 * @param {string} propertyId
	 * @param {PropertyCategory} category
	 * @returns {Promise<void>}
	 */
	async #handleCategoryChange(propertyId, category) {
		this.setState((prevState) => ({
			...prevState,
			categoryErrorByPropertyId: {
				...prevState.categoryErrorByPropertyId,
				[propertyId]: null,
			},
			isUpdatingCategoryByPropertyId: {
				...prevState.isUpdatingCategoryByPropertyId,
				[propertyId]: true,
			},
		}));

		try {
			const property =
				await this.props.propertyService.getEditablePropertyById(
					propertyId
				);

			const accessToken = this.context?.accessToken;

			if (!accessToken) {
				throw new Error(translate('global.error'));
			}

			await homeownerPropertyService.updateProperty(
				propertyId,
				buildPropertyPayload(property, category),
				accessToken
			);

			await this.props.property.refreshPropertyList();
		} catch (error) {
			this.setState((prevState) => ({
				...prevState,
				categoryErrorByPropertyId: {
					...prevState.categoryErrorByPropertyId,
					[propertyId]: translate('global.error'),
				},
			}));
		} finally {
			this.setState((prevState) => ({
				...prevState,
				isUpdatingCategoryByPropertyId: {
					...prevState.isUpdatingCategoryByPropertyId,
					[propertyId]: false,
				},
			}));
		}
	}

	/** @returns {Promise<void>} */
	async #setPropertyImages() {
		this.#propertyResourceRequestId += 1;
		const requestId = this.#propertyResourceRequestId;

		this.setState({
			propertyImages: {},
			watchListDetails: {},
		});

		this.props.property.propertyList.forEach((property) => {
			this.#setPropertyImage(property.id, requestId);

			if (
				getPropertyCategory(property) === PROPERTY_CATEGORIES.WATCH_LIST
			) {
				this.#setWatchListDetails(property.id, requestId);
			}
		});
	}

	/**
	 * @param {string} propertyId
	 * @param {number} requestId
	 * @returns {Promise<void>}
	 */
	async #setPropertyImage(propertyId, requestId) {
		try {
			const image = await this.props.propertyService.getDefaultImage(
				propertyId
			);

			if (requestId !== this.#propertyResourceRequestId) {
				return;
			}

			this.setState((prevState) => ({
				propertyImages: {
					...prevState.propertyImages,
					[propertyId]: toAbsoluteUrl(
						image ?? images.homeowners.properties.streetView
					),
				},
			}));
		} catch (_error) {
			if (requestId !== this.#propertyResourceRequestId) {
				return;
			}

			this.setState((prevState) => ({
				propertyImages: {
					...prevState.propertyImages,
					[propertyId]: images.homeowners.properties.streetView,
				},
			}));
		}
	}

	/**
	 * @param {string} propertyId
	 * @param {number} requestId
	 * @returns {Promise<void>}
	 */
	async #setWatchListDetails(propertyId, requestId) {
		let details = null;
		let rawProperty = /** @type {WatchListRawProperty | null} */ (null);
		let valuations = /** @type {ValuationCollection | null} */ (null);

		try {
			const response = await getWatchListDetails(
				this.props.propertyService,
				propertyId
			);

			details = response.details;
			rawProperty = response.rawProperty;
			valuations = response.valuations;
		} catch (_error) {
			// Keep the list usable even if optional watch-list enrichment fails.
		}

		if (requestId !== this.#propertyResourceRequestId) {
			return;
		}

		const editableDetails =
			/** @type {EditablePropertyFields & WatchListRawDetails} */ (
				details?.details ?? {}
			);
		const rawDetails = rawProperty?.details;

		this.setState((prevState) => ({
			watchListDetails: {
				...prevState.watchListDetails,
				[propertyId]: {
					baths: pickFirstValue(
						editableDetails.numberOfBaths,
						editableDetails.rooms?.bathrooms,
						rawProperty?.number_of_baths,
						rawProperty?.numberOfBaths,
						rawProperty?.bathrooms,
						rawProperty?.bathroom_count,
						rawProperty?.bathroomCount,
						rawProperty?.propertyDetails?.numberOfBaths,
						rawProperty?.propertyDetails?.bathroomCount,
						rawDetails?.number_of_baths,
						rawDetails?.numberOfBaths,
						rawDetails?.rooms?.bathrooms
					),
					beds: pickFirstValue(
						editableDetails.bedrooms,
						editableDetails.rooms?.bedrooms,
						rawProperty?.number_of_bedrooms,
						rawProperty?.numberOfBedrooms,
						rawProperty?.bedrooms,
						rawProperty?.bedroom_count,
						rawProperty?.bedroomCount,
						rawProperty?.propertyDetails?.numberOfBedrooms,
						rawProperty?.propertyDetails?.bedroomCount,
						rawDetails?.number_of_bedrooms,
						rawDetails?.numberOfBedrooms,
						rawDetails?.rooms?.bedrooms
					),
					price: getLatestValuationAmount(valuations),
					sqft: pickFirstValue(
						editableDetails.lotSizeSquareFootage,
						editableDetails.size?.land?.squareFootage,
						rawProperty?.lot_size_square_feet,
						rawProperty?.lotsizeSquareFeet,
						rawProperty?.square_footage,
						rawProperty?.squareFootage,
						rawProperty?.sqft,
						rawProperty?.propertyDetails?.lotsizeSquareFeet,
						rawProperty?.propertyDetails?.squareFootage,
						rawDetails?.lot_size_square_feet,
						rawDetails?.lotSizeSquareFootage,
						rawDetails?.size?.land?.squareFootage
					),
				},
			},
		}));
	}

	/**
	 * @returns {Record<PropertyCategory | 'uncategorized', Property[]>}
	 */
	#getBuckets() {
		/** @type {Record<PropertyCategory | 'uncategorized', Property[]>} */
		const buckets = CATEGORY_CONFIG.reduce((acc, item) => {
			acc[item.key] = [];
			return acc;
		}, /** @type {Record<PropertyCategory | 'uncategorized', Property[]>} */ ({}));

		this.props.property.propertyList.forEach((property) => {
			const category = getPropertyCategory(property);
			buckets[category].push(property);
		});

		return buckets;
	}

	/**
	 * @param {Property} property
	 * @returns {void}
	 */
	#openProperty(property) {
		this.props.history.push(
			paths.app.homeowner.properties.scoped.marketValuation.replace(
				':propertyId',
				property.id
			)
		);
	}

	/**
	 * @param {Property} property
	 * @returns {JSX.Element}
	 */
	#renderCardContent(property) {
		return (
			<React.Fragment>
				<section className="border border-grey-300 rounded overflow-hidden mb-3">
					<img
						alt="Property"
						className="w-full h-[220px] object-cover block"
						src={
							this.state.propertyImages[property.id] ??
							images.homeowners.properties.streetView
						}
					/>
				</section>
				{property.title ? (
					<section className="grid grid-cols-2 border border-grey-300 rounded overflow-hidden">
						<section className="border-r border-grey-300">
							<header className="bg-theme-light text-theme-fg-light px-2 py-2 text-sm font-semibold">
								{translate(
									'homeowner.pages.properties.scoped.labels.property_name'
								)}
							</header>
							<p className="px-2 py-2 text-sm text-black mb-0">
								{property.title}
							</p>
						</section>
						<section>
							<header className="bg-theme-light text-theme-fg-light px-2 py-2 text-sm font-semibold">
								{translate(
									'homeowner.pages.properties.scoped.labels.address'
								)}
							</header>
							<p className="px-2 py-2 text-sm text-black mb-0">
								{formatAddressToString(property.address)}
							</p>
						</section>
					</section>
				) : (
					<section className="border border-grey-300 rounded overflow-hidden">
						<header className="bg-theme-light text-theme-fg-light px-3 py-2 text-sm font-semibold">
							{translate(
								'homeowner.pages.properties.scoped.labels.address'
							)}
						</header>
						<p className="px-3 py-2 text-sm text-black mb-0">
							{formatAddressToString(property.address)}
						</p>
					</section>
				)}
				<PropertyMap
					address={property.address}
					className="property-map mt-3 mb-0"
					embedWithoutKey={true}
				/>
			</React.Fragment>
		);
	}

	/**
	 * @param {Property} property
	 * @returns {JSX.Element}
	 */
	#renderCategoryField(property) {
		return (
			<BaseForm
				initialValues={{ category: '' }}
				noRequiredText={true}
				onSubmit={() => undefined}
			>
				<FieldSelect
					inputClass="mb-0"
					label={translate(
						'homeowner.pages.properties.form.category_label'
					)}
					name="category"
					noDefaultOption={true}
					onChange={(
						/** @type {React.ChangeEvent<HTMLSelectElement>} */ event
					) => {
						if (!event.target.value) {
							return;
						}

						this.#handleCategoryChange(
							property.id,
							/** @type {PropertyCategory} */ (event.target.value)
						);
					}}
					required={true}
				>
					<SelectOptionList
						values={{
							[PROPERTY_CATEGORIES.PRIMARY_HOME]: translate(
								'homeowner.pages.properties.form.category_options.primary_home'
							),
							[PROPERTY_CATEGORIES.SECOND_HOME]: translate(
								'homeowner.pages.properties.form.category_options.second_home'
							),
							[PROPERTY_CATEGORIES.FORMER_HOME]: translate(
								'homeowner.pages.properties.form.category_options.former_home'
							),
							[PROPERTY_CATEGORIES.FRIENDS_OR_FAMILY_HOME]:
								translate(
									'homeowner.pages.properties.form.category_options.friends_or_family_home'
								),
							[PROPERTY_CATEGORIES.WATCH_LIST]: translate(
								'homeowner.pages.properties.form.category_options.watch_list'
							),
						}}
					/>
				</FieldSelect>
			</BaseForm>
		);
	}

	/**
	 * @param {Property} property
	 * @returns {JSX.Element}
	 */
	#renderPropertyCard(property) {
		const isUncategorized =
			getPropertyCategory(property) === UNCATEGORIZED_CATEGORY_KEY;
		const isUpdating = Boolean(
			this.state.isUpdatingCategoryByPropertyId[property.id]
		);
		const categoryError =
			this.state.categoryErrorByPropertyId[property.id] ?? null;
		const cardContent = this.#renderCardContent(property);

		if (!isUncategorized) {
			return (
				<button
					className="w-full flex-1 flex flex-col justify-start border border-grey-300 rounded-lg bg-white p-3 text-left hover:bg-grey-100 overflow-hidden align-top"
					key={property.id}
					onClick={() => this.#openProperty(property)}
					type="button"
				>
					{cardContent}
				</button>
			);
		}

		return (
			<section
				className="w-full flex-1 flex flex-col justify-start border border-grey-300 rounded-lg bg-white p-3 text-left overflow-hidden align-top"
				key={property.id}
			>
				{this.#renderCategoryField(property)}
				<section className="min-h-[24px] mb-3">
					{renderCategoryStatus(isUpdating, categoryError)}
				</section>
				<button
					className="w-full flex-1 flex flex-col justify-start text-left hover:bg-grey-100 overflow-hidden align-top"
					disabled={isUpdating}
					onClick={() => this.#openProperty(property)}
					type="button"
				>
					{cardContent}
				</button>
			</section>
		);
	}

	/**
	 * @param {Property} property
	 * @returns {JSX.Element}
	 */
	#renderWatchListProperty(property) {
		const details = this.state.watchListDetails[property.id] ?? {};
		const price = details.price
			? formatCentsForDisplay(details.price, 0)
			: '--';

		return (
			<button
				className="block bg-white p-0 text-left hover:bg-grey-100 rounded-lg overflow-hidden"
				key={property.id}
				onClick={() => this.#openProperty(property)}
				style={{ width: '765px' }}
				type="button"
			>
				<section
					className="items-center"
					style={{
						columnGap: '32px',
						display: 'grid',
						gridTemplateColumns: '165px 250px 70px 70px 90px',
					}}
				>
					<section
						className="rounded border border-grey-300 bg-grey-100 p-2 flex items-center justify-center"
						style={{ height: '115px', width: '165px' }}
					>
						<img
							alt={translate(
								'homeowner.pages.properties.scoped.labels.watch_list_alt'
							)}
							className="object-cover block border border-black rounded-sm"
							src={
								this.state.propertyImages[property.id] ??
								images.homeowners.properties.streetView
							}
							style={{
								height: '100%',
								maxHeight: '99px',
								maxWidth: '149px',
								width: '100%',
							}}
						/>
					</section>
					<section>
						<p className="text-2xl font-bold text-black mb-1">
							{price}
						</p>
						<p className="text-sm text-grey-700 mb-0">
							{formatAddressToString(property.address)}
						</p>
					</section>
					<section className="text-center">
						<p className="text-2xl font-bold text-black mb-0">
							{formatWatchListValue(details.beds)}
						</p>
						<p className="text-sm text-grey-700 mb-0">
							{translate(
								'homeowner.pages.properties.scoped.labels.beds'
							)}
						</p>
					</section>
					<section className="text-center">
						<p className="text-2xl font-bold text-black mb-0">
							{formatWatchListValue(details.baths)}
						</p>
						<p className="text-sm text-grey-700 mb-0">
							{translate(
								'homeowner.pages.properties.scoped.labels.baths'
							)}
						</p>
					</section>
					<section className="text-center">
						<p className="text-2xl font-bold text-black mb-0">
							{formatWatchListValue(details.sqft)}
						</p>
						<p className="text-sm text-grey-700 mb-0">
							{translate(
								'homeowner.pages.properties.scoped.labels.sqft'
							)}
						</p>
					</section>
				</section>
			</button>
		);
	}

	/**
	 * @param {CategoryConfig} category
	 * @param {Property[]} properties
	 * @returns {JSX.Element}
	 */
	#renderCategory(category, properties) {
		const isWatchList = category.key === PROPERTY_CATEGORIES.WATCH_LIST;
		const isUncategorized = category.key === UNCATEGORIZED_CATEGORY_KEY;

		if (isWatchList) {
			return (
				<section
					className="border border-grey-300 rounded bg-white p-5 mb-10"
					key={category.key}
				>
					{properties.length ? null : (
						<header className="text-center mb-8">
							<p className="text-2xl text-black mb-0">
								{translate(
									'homeowner.pages.properties.scoped.empty.watch_list_header'
								)}
							</p>
						</header>
					)}
					<header className="flex flex-wrap items-center justify-between gap-3 mb-2">
						<h2 className="h-ms-4 mb-0">
							{translate(
								'homeowner.pages.properties.scoped.category.watch_list'
							)}
						</h2>
						{renderCategoryAddButton(category)}
					</header>
					<div className="h-[3px] bg-theme-primary mb-5" />
					<section className="overflow-x-auto">
						<section
							className="space-y-10"
							style={{ minWidth: '765px' }}
						>
							{properties.map((property) =>
								this.#renderWatchListProperty(property)
							)}
						</section>
					</section>
				</section>
			);
		}

		return (
			<section
				className="border border-grey-300 rounded bg-white p-5 mb-10"
				key={category.key}
			>
				<header className="flex flex-wrap items-center justify-between gap-3 mb-2">
					<h2 className="h-ms-4 mb-0">{category.title}</h2>
					{renderCategoryAddButton(category)}
				</header>
				{isUncategorized ? (
					<section className="mb-4 form-alert form-alert-warning w-fit max-w-xl">
						<h3 className="title">
							{translate('global.messages.warning')}
						</h3>
						<p className="text mb-0">
							{translate(
								'homeowner.pages.properties.scoped.category.uncategorized_warning'
							)}
						</p>
					</section>
				) : null}
				<div className="h-[3px] bg-theme-primary mb-5" />
				<section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
					{properties.map((property) => (
						<section key={property.id}>
							{this.#renderPropertyCard(property)}
						</section>
					))}
				</section>
			</section>
		);
	}

	/**
	 * Balance properties into rows of at most three, placing any shorter row
	 * first so four and five properties render as 2+2 and 2+3.
	 * @param {{property: Property, category: CategoryConfig}[]} properties
	 * @returns {{property: Property, category: CategoryConfig}[][]}
	 */
	static getPropertyRows(properties) {
		const rowCount = Math.ceil(properties.length / 3);

		if (!rowCount) {
			return [];
		}

		const minimumRowSize = Math.floor(properties.length / rowCount);
		const largerRowCount = properties.length % rowCount;
		const rowSizes = Array.from(
			{ length: rowCount },
			function getRowSize(unused, index) {
				return index >= rowCount - largerRowCount
					? minimumRowSize + 1
					: minimumRowSize;
			}
		);
		let offset = 0;

		return rowSizes.map((rowSize) => {
			const row = properties.slice(offset, offset + rowSize);
			offset += rowSize;
			return row;
		});
	}

	/**
	 * @param {Record<PropertyCategory | 'uncategorized', Property[]>} buckets
	 * @returns {JSX.Element}
	 */
	#renderPropertyGrid(buckets) {
		const properties = CATEGORY_CONFIG.filter(
			(category) => category.key !== PROPERTY_CATEGORIES.WATCH_LIST
		).flatMap((category) =>
			buckets[category.key].map((property) => ({ category, property }))
		);
		const hasUncategorized = buckets[UNCATEGORIZED_CATEGORY_KEY].length > 0;

		return (
			<section className="border border-grey-300 rounded bg-white p-5 mb-10">
				<header className="flex flex-wrap items-center justify-between gap-3 mb-5">
					<h1 className="hl-ms-6 mb-0">{'Properties'}</h1>
					{PropertiesHub.renderAddPropertyMenu()}
				</header>
				{hasUncategorized ? (
					<section className="mb-5 form-alert form-alert-warning w-fit max-w-xl">
						<h2 className="title">
							{translate('global.messages.warning')}
						</h2>
						<p className="text mb-0">
							{translate(
								'homeowner.pages.properties.scoped.category.uncategorized_warning'
							)}
						</p>
					</section>
				) : null}
				<section className="space-y-8">
					{PropertiesHub.getPropertyRows(properties).map((row) => (
						<section
							className={PropertiesHub.getPropertyRowClassName(
								row.length
							)}
							key={row
								.map(({ property }) => property.id)
								.join('-')}
						>
							{row.map(({ category, property }) => (
								<section
									className="flex min-w-0 flex-col"
									key={property.id}
								>
									<h2 className="h-ms-4 mb-3">
										{category.title}
									</h2>
									{this.#renderPropertyCard(property)}
								</section>
							))}
						</section>
					))}
				</section>
			</section>
		);
	}

	/** @returns {JSX.Element} */
	static renderAddPropertyMenu() {
		const addCategories = CATEGORY_CONFIG.filter(
			(category) =>
				category.key !== UNCATEGORIZED_CATEGORY_KEY &&
				category.key !== PROPERTY_CATEGORIES.WATCH_LIST
		);

		return (
			<details className="dropdown relative">
				<summary className="button button-outline cursor-pointer list-none">
					{translate('homeowner.pages.properties.scoped.labels.add')}
				</summary>
				<ul className="dropdown-menu active right-0 left-auto">
					<li>
						{addCategories.map((category) => (
							<Link
								key={category.key}
								to={{
									pathname:
										paths.app.homeowner.properties.add,
									state: { category: category.key },
								}}
							>
								{category.title}
							</Link>
						))}
					</li>
				</ul>
			</details>
		);
	}

	/**
	 * @param {number} propertyCount
	 * @returns {string}
	 */
	static getPropertyRowClassName(propertyCount) {
		if (propertyCount === 2) {
			return 'grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3';
		}

		if (propertyCount === 3) {
			return 'grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3';
		}

		return 'grid grid-cols-1 gap-8';
	}

	/** @returns {JSX.Element} */
	render() {
		const buckets = this.#getBuckets();
		const watchListCategory = /** @type {CategoryConfig} */ (
			CATEGORY_CONFIG.find(
				(category) => category.key === PROPERTY_CATEGORIES.WATCH_LIST
			)
		);

		return (
			<section className="mt-6 sm:mt-8">
				{this.#renderPropertyGrid(buckets)}
				{this.#renderCategory(
					watchListCategory,
					buckets[PROPERTY_CATEGORIES.WATCH_LIST]
				)}
			</section>
		);
	}
}

export default withPropertyService(withProperty(PropertiesHub));
